/**
 * Get the pixel coordinates of the caret position in a textarea or input element
 * This creates a hidden mirror div to calculate the exact position
 */
export function getCaretCoordinates(
  element: HTMLTextAreaElement | HTMLInputElement,
  position?: number
): { top: number; left: number; height: number } {
  const isInput = element.nodeName === 'INPUT';
  const actualPosition = position ?? element.selectionEnd ?? 0;

  // Create a mirror div to calculate caret position
  const mirror = document.createElement('div');
  const computed = window.getComputedStyle(element);
  const isRTL = computed.direction === 'rtl';

  // Copy all relevant CSS properties to the mirror
  mirror.style.position = 'absolute';
  mirror.style.visibility = 'hidden';
  mirror.style.whiteSpace = isInput ? 'nowrap' : 'pre-wrap';
  mirror.style.wordWrap = 'break-word';

  // Transfer the element's properties to the div
  const properties = [
    'boxSizing',
    'width',
    'height',
    'overflowX',
    'overflowY',
    'borderTopWidth',
    'borderRightWidth',
    'borderBottomWidth',
    'borderLeftWidth',
    'paddingTop',
    'paddingRight',
    'paddingBottom',
    'paddingLeft',
    'fontStyle',
    'fontVariant',
    'fontWeight',
    'fontStretch',
    'fontSize',
    'fontSizeAdjust',
    'lineHeight',
    'fontFamily',
    'textAlign',
    'textTransform',
    'textIndent',
    'textDecoration',
    'letterSpacing',
    'wordSpacing',
    'tabSize'
  ];

  properties.forEach((prop) => {
    // @ts-expect-error - Dynamic property access
    mirror.style[prop] = computed[prop];
  });

  document.body.appendChild(mirror);

  // Get text up to caret position
  const textBeforeCaret = element.value.substring(0, actualPosition);

  if (isInput) {
    mirror.textContent = textBeforeCaret;
  } else {
    // For textarea, handle newlines properly
    mirror.textContent = textBeforeCaret;
  }

  // Create a span to represent the caret
  const span = document.createElement('span');
  span.textContent = element.value.substring(actualPosition) || '.';
  mirror.appendChild(span);

  // Get the position of the span (caret position)
  const rect = element.getBoundingClientRect();
  const spanRect = span.getBoundingClientRect();
  const mirrorRect = mirror.getBoundingClientRect();

  // Calculate coordinates relative to the element
  const coordinates = {
    top: rect.top + (spanRect.top - mirrorRect.top) + element.scrollTop,
    left: isRTL
      ? rect.left + rect.width - (spanRect.left - mirrorRect.left)
      : rect.left + (spanRect.left - mirrorRect.left),
    height: spanRect.height
  };

  // Clean up
  document.body.removeChild(mirror);

  return coordinates;
}
